import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GrantApplicationForm = ({ user, onClose, isModal = false }) => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [states, setStates] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    ssn: '',
    state_id_document: '',
    state_id_filename: '',
    state_code: '',
    zip_code: '',
    address: '',
    city: '',
    estimated_cost: 0,
    why_chef: '',
    cuisine_type: '',
    business_plan: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [idFilePreview, setIdFilePreview] = useState(null);

  useEffect(() => {
    // Fetch current user if not provided as prop
    if (!user) {
      fetchCurrentUser();
    } else {
      setCurrentUser(user);
      setFormData(prev => ({
        ...prev,
        full_name: user.full_name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/users/me`);
      setCurrentUser(response.data);
      setFormData(prev => ({
        ...prev,
        full_name: response.data.full_name || '',
        email: response.data.email || ''
      }));
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/compliance/states`);
      setStates(response.data.states || []);
    } catch (error) {
      console.error('Failed to fetch states:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${API}/grants/apply`, formData);
      setSuccess(true);
      setTimeout(() => {
        if (onClose) {
          onClose();
        } else {
          navigate('/chef/dashboard');
        }
      }, 3000);
    } catch (error) {
      // Handle validation errors (array of objects) or simple string errors
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic validation errors
        const errorMessages = detail.map(err => `${err.loc.join('.')}: ${err.msg}`).join(', ');
        setError(errorMessages);
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to submit application. Please check all required fields.');
      }
    }
    setSubmitting(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Format SSN with dashes as user types
    if (name === 'ssn') {
      const cleaned = value.replace(/\D/g, '');
      let formatted = cleaned;
      if (cleaned.length >= 3) {
        formatted = cleaned.slice(0, 3) + '-' + cleaned.slice(3);
      }
      if (cleaned.length >= 5) {
        formatted = cleaned.slice(0, 3) + '-' + cleaned.slice(3, 5) + '-' + cleaned.slice(5, 9);
      }
      setFormData({ ...formData, [name]: formatted });
      return;
    }
    
    setFormData({ ...formData, [name]: value });
    
    // Auto-update estimated cost based on state
    if (name === 'state_code') {
      // Approximate cottage food permit costs by state
      const costs = {
        'IL': 50, 'CA': 150, 'NY': 125, 'TX': 0, 'FL': 0, 'WY': 0,
        'ND': 0, 'UT': 0, 'ME': 50, 'OK': 0, 'AK': 0
      };
      setFormData(prev => ({ ...prev, estimated_cost: costs[value] || 100 }));
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a valid ID document (JPG, PNG, or PDF)');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }
    
    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({
        ...prev,
        state_id_document: reader.result,
        state_id_filename: file.name
      }));
      setIdFilePreview(file.name);
      setError('');
    };
    reader.readAsDataURL(file);
  };

  if (success) {
    const successContent = (
      <div className="bg-white rounded-xl max-w-md w-full p-8 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-5xl">🎉</span>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h2>
        <p className="text-gray-600 mb-4">
          Thank you for applying for the Lambalia Cottage Permit Grant! We'll review your application and get back to you within 5-7 business days.
        </p>
        <p className="text-sm text-gray-500">
          Check your email for confirmation and next steps.
        </p>
      </div>
    );
    
    if (isModal) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          {successContent}
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center">
        {successContent}
      </div>
    );
  }

  const formContent = (
    <div className="bg-white rounded-xl max-w-3xl w-full p-6 my-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">🌱 Lambalia Grant Application</h2>
        <p className="text-gray-600 mb-6">
          Apply for a grant to cover your cottage food permit fees. Lambalia will pay your state directly!
        </p>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-800 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="+1 (555) 123-4567"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Social Security Number (SSN) *
              </label>
              <input
                type="text"
                name="ssn"
                value={formData.ssn}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="123-45-6789"
                maxLength="11"
                pattern="\d{3}-\d{2}-\d{4}"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Format: 123-45-6789 (required for identity verification)</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🪪 State ID or Driver's License * <span className="text-red-500">(Required)</span>
            </label>
            <p className="text-sm text-gray-600 mb-3">
              Upload a clear photo or scan of your valid state-issued ID or driver's license. 
              Accepted formats: JPG, PNG, PDF (Max 5MB)
            </p>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,application/pdf"
              onChange={handleFileUpload}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            {idFilePreview && (
              <p className="text-sm text-green-600 mt-2">
                ✅ Uploaded: {idFilePreview}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
              <select
                name="state_code"
                value={formData.state_code}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                size="1"
                required
              >
                <option value="">Select your state</option>
                {states.map(state => (
                  <option key={state.code} value={state.code}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code *</label>
              <input
                type="text"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Street Address *</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estimated Permit Cost * 
              <span className="text-xs text-gray-500 ml-2">(Auto-filled based on state)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                type="number"
                name="estimated_cost"
                value={formData.estimated_cost}
                onChange={handleChange}
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                min="0"
                step="0.01"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type of Cuisine *</label>
            <input
              type="text"
              name="cuisine_type"
              value={formData.cuisine_type}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="e.g., Nigerian, Italian, Vegan Desserts"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Why do you want to become a home chef? *
            </label>
            <textarea
              name="why_chef"
              value={formData.why_chef}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              rows="4"
              placeholder="Tell us your story, your passion for food, and how this grant will help you..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business Plan (Optional)
            </label>
            <textarea
              name="business_plan"
              value={formData.business_plan}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              rows="3"
              placeholder="Briefly describe your business goals, target customers, and what makes you unique..."
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>💡 Grant Details:</strong> If approved, Lambalia will pay the permit fees directly to your state's health department. 
              You'll receive instructions on how to complete your permit application once the payment is processed.
            </p>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-medium disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Application'}
            </button>
            {(onClose || !isModal) && (
              <button
                type="button"
                onClick={() => onClose ? onClose() : navigate('/chef/dashboard')}
                className="flex-1 bg-gray-200 hover:bg-gray-300 py-3 rounded-lg font-medium"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
        {formContent}
      </div>
    );
  }
  
  return formContent;
};

export default GrantApplicationForm;
